'use client';

import { useState } from 'react';
import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { ArrivalOrderDayColumn, ARRIVAL_ORDER_COLLAPSED_LIMIT } from './arrival-order-day-column.component';

import { Button } from '@/components/ui/button/button.component';
import {
  SlotsNextArrow,
  SlotsPrevArrow,
} from '@/app/(main)/(pages)/_components/schedules/slots-pagination-arrows.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { ScheduleSlotsDay } from '@/app/(main)/(pages)/_components/schedules/_hooks/use-schedule-slots.hook';
import { SchedulesSearchSlot } from '@/app/(main)/(pages)/_components/schedules/_types/schedule-slot.type';
import { cn } from '@/utils/cn-tw-merge';
import { dateFormatter } from '@/utils/formatters/date';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

interface ArrivalOrderSlotsGridProps {
  days: ScheduleSlotsDay[];
  hasMore: boolean;
  offset: number;
  partnerId: string;
  price: number;
  busySlotIds?: ReadonlySet<string>;
  className?: string;
  slotClassName?: string;
  onPrev: () => void;
  onNext: () => void;
  onSeeMore?: () => void;
  onSelectSlot: (slot: SchedulesSearchSlot) => void;
}

export function ArrivalOrderSlotsGrid({
  days,
  hasMore,
  offset,
  partnerId,
  price,
  busySlotIds,
  className,
  slotClassName,
  onPrev,
  onNext,
  onSeeMore,
  onSelectSlot,
}: ArrivalOrderSlotsGridProps) {
  const [expandedDate, setExpandedDate] = useState<string | null>(null);
  const analytics = getAnalyticsPlugin();

  const toggleExpand = (date: string) => {
    setExpandedDate((current) => (current === date ? null : date));
  };

  const handleSlotClick = (slot: SchedulesSearchSlot) => {
    analytics.track(AnalyticsEnum.CLICK_ARRIVAL_ORDER_SLOT, {
      partner_id: partnerId,
      data: slot.date,
      faixa_horario: `${dateFormatter(slot.start, 'HH:mm')} às ${dateFormatter(slot.end, 'HH:mm')}`,
      price,
    });

    onSelectSlot(slot);
  };

  const handlePrev = () => {
    analytics.track(AnalyticsEnum.CLICK_ARRIVAL_ORDER_DATE_NAV, { partner_id: partnerId, direcao: 'anterior' });
    onPrev();
  };

  const handleNext = () => {
    analytics.track(AnalyticsEnum.CLICK_ARRIVAL_ORDER_DATE_NAV, { partner_id: partnerId, direcao: 'proxima' });
    onNext();
  };

  if (days.length === 0) {
    return (
      <div
        className={cn('flex flex-col gap-4 w-full', className)}
        data-testid={DataTestIdEnum.SEARCH_CARD_ARRIVAL_ORDER_GRID}
      >
        <Typography variant={TypographyVariant.BODY_TEXT} className="w-full text-center text-gray-500 py-6">
          Nenhum horário disponível.
        </Typography>
      </div>
    );
  }

  const canSeeMore = Boolean(onSeeMore)
    && days.some((day) => day.slots.length > ARRIVAL_ORDER_COLLAPSED_LIMIT);

  return (
    <div
      className={cn('flex flex-col gap-4 w-full', className)}
      data-testid={DataTestIdEnum.SEARCH_CARD_ARRIVAL_ORDER_GRID}
    >
      <div className="flex items-start justify-center w-full">
        <SlotsPrevArrow
          disabled={offset === 0}
          testId={DataTestIdEnum.SEARCH_CARD_ARRIVAL_ORDER_PREVIOUS_ARROW_BUTTON}
          onClick={handlePrev}
        />
        <div className="flex-1 flex gap-4 items-start min-w-0">
          {days.map((day) => (
            <ArrivalOrderDayColumn
              key={day.date}
              date={day.date}
              slots={day.slots}
              expanded={expandedDate === day.date}
              busySlotIds={busySlotIds}
              slotClassName={slotClassName}
              onToggleExpand={() => toggleExpand(day.date)}
              onSlotClick={handleSlotClick}
            />
          ))}
        </div>
        <SlotsNextArrow
          disabled={!hasMore}
          testId={DataTestIdEnum.SEARCH_CARD_ARRIVAL_ORDER_NEXT_ARROW_BUTTON}
          onClick={handleNext}
        />
      </div>
      {canSeeMore && (
        <Button
          variant="text"
          color="primary"
          startIcon={faPlus}
          startIconSize="xs"
          startIconClassName="text-xs text-green-500"
          className="self-center gap-2 px-4 py-2"
          data-testid={DataTestIdEnum.SEARCH_CARD_ARRIVAL_ORDER_SEE_MORE}
          onClick={onSeeMore}
        >
          Ver outros horários
        </Button>
      )}
    </div>
  );
}
